import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import PrimaryCta from 'components/Buttons/PrimaryCta';
import {
  isSubtotalBelowMinimumSelector,
  isDeliveryWithNoAddressSelector,
  selectGroupOrderErrorIs404,
  selectGroupOrderErrorIs1006
} from 'bento-ordering/cart/selectors/';
import {
  CART_CHECKOUT_LABEL,
  CART_INVALID_ADDRESS_LABEL,
  CART_INVALID_FULFILLMENT_TIME_LABEL,
  CART_INVALID_ORDER_MINIMUM_LABEL,
  CART_PICKUP_ADDRESS_CHANGED_LABEL,
  GROUP_ORDER_CHECKOUT_CONFIRMATION_MODAL
} from 'constants/cart';
import { GROUP_ORDER_CHECKOUT_CONFIRMATION } from 'bento-ordering/group_ordering/constants/groupOrder';

import resetPickupAddress from 'bento-ordering/cart/actions/resetPickupAddress';
import GuestsStatusCheckoutInfo from 'bento-ordering/group_ordering/components/GuestsStatusCheckoutInfo';
import GroupOrderErrorToast from 'bento-ordering/group_ordering/components/GroupOrderErrorToast';
import CartDrawerPromotionsWrapper from 'bento-ordering/cart/containers/CartDrawerPromotionsWrapper';

import {
  useIsGroupOrderAdminDiner,
  useRedirectToOOMenu
} from 'bento-ordering/group_ordering/hooks';
import useShouldDisplayPromotionsOnCart from 'bento-ordering/promotions/hooks/useShouldDisplayPromotionsOnCart';
import { useHasInvestorDayFeatureFlag } from 'bento-ordering/utils/featureFlags/hasInvestorDayFeatureFlag';
import { createOrderWithoutCheckout } from 'bento-ordering/investor_day/actions';
import { useSeatIdIsSetOnCart } from 'bento-ordering/investor_day/hooks';
import { SET_SEAT_ID_MODAL } from 'bento-ordering/investor_day/constants';
import CreateOrderErrorToast from 'bento-ordering/investor_day/components/CreateOrderErrorToast';
import { usePreviewModeDataContext } from 'contexts/PreviewModeDataContext/index';
import { heapClearProperties } from 'user_analytics/heapMain';

const getCheckoutButtonLabel = (
  isDeliveryWithNoAddress,
  cartValidationError,
  cartValidationState,
  errorIs404NotFound,
  errorIs1006WSAbnormalClosure,
  hasInvestorDayFeature
) => {
  if (isDeliveryWithNoAddress) {
    return 'Enter Address To Continue';
  } else if (
    cartValidationError &&
    /address has changed/.test(cartValidationError)
  ) {
    return CART_PICKUP_ADDRESS_CHANGED_LABEL;
  } else if (
    cartValidationError &&
    cartValidationError ===
      'The cart has been idle and can no longer meet the fulfillment time.'
  ) {
    return CART_INVALID_FULFILLMENT_TIME_LABEL;
  } else if (cartValidationError && /order minimum/.test(cartValidationError)) {
    return CART_INVALID_ORDER_MINIMUM_LABEL;
  } else if (cartValidationError && !cartValidationState.addressError) {
    return CART_INVALID_ADDRESS_LABEL;
  } else if (errorIs404NotFound) {
    return GROUP_ORDER_CHECKOUT_CONFIRMATION.startover;
  } else if (errorIs1006WSAbnormalClosure) {
    return GROUP_ORDER_CHECKOUT_CONFIRMATION.retry;
  } else if (hasInvestorDayFeature) {
    return 'Place Order';
  }
  return CART_CHECKOUT_LABEL;
};

const CartPageCheckoutContainer = ({
  dispatch,
  isDeliveryWithNoAddress,
  cartValidationError,
  isTotalBelowMinimum,
  isValidatingCartItemInventory,
  checkoutUrl,
  dataTestId,
  id,
  cartValidation,
  toggleIsOpen,
  disableCheckoutButton = false
}) => {
  const { isPreview } = usePreviewModeDataContext();
  const isGroupOrderAdminDiner = useIsGroupOrderAdminDiner();
  const shouldDisplayPromotionsOnCart = useShouldDisplayPromotionsOnCart();
  const errorIs404NotFound = useSelector(selectGroupOrderErrorIs404);
  const errorIs1006WSAbnormalClosure = useSelector(selectGroupOrderErrorIs1006);
  const redirectToOO = useRedirectToOOMenu();
  const hasInvestorDayFeature = useHasInvestorDayFeatureFlag();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [createOrderError, setCreateOrderError] = React.useState('');

  const seatIdIsSetOnCart = useSeatIdIsSetOnCart();

  const handleCreateOrderWithoutCheckout = async () => {
    if (!seatIdIsSetOnCart) {
      return toggleIsOpen(SET_SEAT_ID_MODAL);
    } else {
      setIsSubmitting(true);
      return dispatch(
        createOrderWithoutCheckout(setIsSubmitting, setCreateOrderError)
      );
    }
  };

  const handlePrimaryCtaClick = () => {
    if (errorIs404NotFound) {
      redirectToOO();
      return;
    }
    if (errorIs1006WSAbnormalClosure) {
      window.location.reload(true);
      return;
    }
    if (isGroupOrderAdminDiner) {
      toggleIsOpen(GROUP_ORDER_CHECKOUT_CONFIRMATION_MODAL);
    } else if (hasInvestorDayFeature) {
      return handleCreateOrderWithoutCheckout();
    } else {
      window.location.replace(checkoutUrl);

      // Clearing cookie after redirect to capture that event as well
      heapClearProperties();
    }
  };

  const handleResetPickupAddress = async () => {
    dispatch(resetPickupAddress());
  };

  const label = getCheckoutButtonLabel(
    isDeliveryWithNoAddress,
    cartValidationError,
    cartValidation,
    errorIs404NotFound,
    errorIs1006WSAbnormalClosure,
    hasInvestorDayFeature
  );

  return (
    <>
      {shouldDisplayPromotionsOnCart && <CartDrawerPromotionsWrapper />}
      <GroupOrderErrorToast />
      {hasInvestorDayFeature && (
        <CreateOrderErrorToast error={createOrderError} />
      )}
      {label === CART_PICKUP_ADDRESS_CHANGED_LABEL ? (
        <PrimaryCta
          id={id} // id is used for a11y jump to checkout button
          component="a"
          handleClick={handleResetPickupAddress}
          label={label}
          fullWidth
          dataTestId={dataTestId}
        />
      ) : (
        <PrimaryCta
          id={id}
          component="a"
          handleClick={handlePrimaryCtaClick}
          label={label}
          fullWidth
          disabled={
            isPreview ||
            isTotalBelowMinimum ||
            isDeliveryWithNoAddress ||
            !!cartValidationError ||
            isValidatingCartItemInventory ||
            isSubmitting ||
            createOrderError ||
            disableCheckoutButton
          }
          dataTestId={dataTestId}
        />
      )}
      {isGroupOrderAdminDiner && <GuestsStatusCheckoutInfo />}
    </>
  );
};

CartPageCheckoutContainer.propTypes = {
  dispatch: PropTypes.func,
  isDeliveryWithNoAddress: PropTypes.bool,
  cartValidationError: PropTypes.string,
  isTotalBelowMinimum: PropTypes.bool,
  isValidatingCartItemInventory: PropTypes.bool,
  checkoutUrl: PropTypes.string,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  cartValidation: PropTypes.object,
  toggleIsOpen: PropTypes.func.isRequired,
  disableCheckoutButton: PropTypes.bool
};

CartPageCheckoutContainer.defaultProps = {
  dataTestId: 'cart-checkout-button',
  id: 'cart-checkout-button'
};

const mapStateToProps = state => {
  const { cart, cartValidation } = state;
  const isDeliveryWithNoAddress = isDeliveryWithNoAddressSelector(cart);
  const isTotalBelowMinimum = isSubtotalBelowMinimumSelector(cart);
  const {
    isValidatingCartItemInventory,
    checkout_url: checkoutUrl,
    cartValidationError
  } = cart;

  return {
    isDeliveryWithNoAddress,
    cartValidationError,
    isTotalBelowMinimum,
    isValidatingCartItemInventory,
    checkoutUrl,
    cartValidation
  };
};

export default connect(mapStateToProps)(CartPageCheckoutContainer);
